import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// ── Configurable fields ─────────────────────────────────────────────
const EFFECTIVE_DATE = 'January 1, 2026'
const LAST_UPDATED = 'February 25, 2026'
const CONTACT_EMAIL = 'support@playsocrates.com'
const LEGAL_ENTITY = 'Bobblehead Sports, LLC'
// ─────────────────────────────────────────────────────────────────────

export default function Privacy() {
  const location = useLocation()
  const backTo = location.state?.from || '/'
  const backLabel = backTo === '/' ? 'Back to home' : 'Back'

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-stone-300">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-300 text-sm mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-stone-500 text-sm mb-2">Effective Date: {EFFECTIVE_DATE}</p>
        <p className="text-stone-500 text-sm mb-8">Last Updated: {LAST_UPDATED}</p>

        <p className="text-sm leading-relaxed mb-6">
          Socrates ("Socrates," "we," "our," or "us") respects your privacy. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your information when
          you use the Socrates mobile application, website, and related services (collectively,
          the "Service").
        </p>
        <p className="text-sm leading-relaxed mb-8">
          By using Socrates, you agree to the practices described in this Privacy Policy.
        </p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Information We Collect</h2>

            <h3 className="text-stone-200 font-medium mt-3 mb-1">A. Information You Provide</h3>
            <p>We may collect information you provide directly, including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Email address (for account creation and login)</li>
              <li>League names or profile identifiers you create</li>
              <li>Communications you send to us (support requests, feedback)</li>
            </ul>
            <p className="mt-2">
              Socrates does not require passwords; authentication is conducted via secure
              email login links.
            </p>

            <h3 className="text-stone-200 font-medium mt-4 mb-1">B. Automatically Collected Information</h3>
            <p>When you use the Service, we may automatically collect:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Device type and operating system</li>
              <li>Browser type</li>
              <li>IP address</li>
              <li>Usage activity (e.g., matchday selections, feature interactions)</li>
              <li>Log data and diagnostic information</li>
            </ul>
            <p className="mt-2">
              This information is used to maintain performance, security, and product improvement.
            </p>

            <h3 className="text-stone-200 font-medium mt-4 mb-1">C. Third-Party Data</h3>
            <p>We rely on third-party providers for:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Authentication services</li>
              <li>Hosting infrastructure</li>
              <li>Analytics and performance monitoring</li>
              <li>Official football match data</li>
            </ul>
            <p className="mt-2">
              We do not control the privacy practices of these third parties. Their use of
              data is governed by their respective privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Provide and operate the Service</li>
              <li>Authenticate and manage user accounts</li>
              <li>Calculate scoring and standings</li>
              <li>Send transactional communications (e.g., login links, matchday reminders)</li>
              <li>Improve platform performance and user experience</li>
              <li>Detect fraud, abuse, or security issues</li>
            </ul>
            <p className="mt-2">We do not sell your personal information.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. Communications</h2>
            <p>We may send you:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Login emails (magic links)</li>
              <li>Matchday reminders</li>
              <li>Service-related notifications</li>
              <li>Administrative updates</li>
            </ul>
            <p className="mt-2">
              You may opt out of non-essential communications, but you cannot opt out of
              transactional or security-related messages necessary for account access.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Data Sharing and Disclosure</h2>
            <p>We do not sell, rent, or trade your personal information.</p>
            <p className="mt-2">We may share information:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>With trusted service providers who assist in operating the platform</li>
              <li>To comply with legal obligations</li>
              <li>To protect the rights, safety, and integrity of Socrates or its users</li>
              <li>In connection with a business transfer (e.g., merger or acquisition)</li>
            </ul>
            <p className="mt-2">
              All third-party providers are required to handle data in accordance with
              applicable privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as
              necessary to:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Provide the Service</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce agreements</li>
            </ul>
            <p className="mt-2">
              If you request deletion of your account, we will delete or anonymize your
              personal information, unless retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Data Security</h2>
            <p>
              We implement reasonable technical and organizational safeguards to protect
              your information.
            </p>
            <p className="mt-2">
              However, no internet-based system is completely secure. We cannot guarantee
              absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have rights including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li>Access to your personal data</li>
              <li>Correction of inaccurate data</li>
              <li>Deletion of your data</li>
              <li>Restriction or objection to certain processing</li>
              <li>Data portability</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at the email listed below.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. Children's Privacy</h2>
            <p>
              Socrates is not intended for individuals under 18 years of age. We do not
              knowingly collect personal information from minors.
            </p>
            <p className="mt-2">
              If we become aware that a minor has created an account, we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. International Users</h2>
            <p>
              If you access Socrates from outside the United States of America, your
              information may be transferred to and processed in jurisdictions where data
              protection laws may differ from those in your country.
            </p>
            <p className="mt-2">
              By using the Service, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. When we do, we will revise
              the "Last Updated" date.
            </p>
            <p className="mt-2">
              Your continued use of the Service after changes become effective constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or your data, please contact:
            </p>
            <ul className="mt-2 space-y-1 text-stone-400">
              <li>Email: {CONTACT_EMAIL}</li>
              <li>Company: {LEGAL_ENTITY}</li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-stone-700 text-center text-stone-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Socrates. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
